import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Typography,
  Stack,
  Divider,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Bookmark as BookmarkIcon,
  TrendingUp as TrendingIcon,
  FiberNew as NewIcon,
} from "@mui/icons-material";
import BlogForm from "../components/blog/BlogForm";
import { useDispatch, useSelector } from "react-redux";
import { deleteBlog, fetchBlogs } from "../redux/slices/blog";
import ConfirmDelete from "../components/blog/confirmdelete";

export default function Blogs() {
  const { data, loading } = useSelector((state) => state.blog);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchBlogs());
  }, []);

  const handleOpen = (post = null) => {
    if (post) {
      setSelected(post);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  const openDelete = (obj) => {
    setSelected(obj);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setSelected(null);
    setDeleteOpen(false);
  };

  if (loading) {
    return (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Loading blogs...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              mb={3}
              color="text.primary"
            >
              Blog Posts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your blog content
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              textTransform: "none",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            Create New Post
          </Button>
        </Box>

        <Grid container spacing={3}>
          {data.map((post) => (
            <Grid size={{ sx: 12, md: 6, lg: 4 }} key={post._id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  overflow: "hidden",
                  background: "background.paper",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                  transition: "all 0.35s ease",
                  position: "relative",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 8px 22px rgba(0,0,0,0.12)",
                  },
                }}
              >
                {post.coverImage && (
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      image={post.coverImage}
                      alt={post.title}
                      sx={{
                        objectFit: "cover",
                        filter: "brightness(95%)",
                        transition: "0.3s",
                        "&:hover": { filter: "brightness(100%)" },
                        maxHeight: 200,
                      }}
                    />

                    {/* Floating Action Buttons */}
                    <Stack
                      direction="row"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        gap: 1,
                        background: "rgba(255,255,255,0.65)",
                        backdropFilter: "blur(6px)",
                        p: 0.5,
                        borderRadius: 2,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(post)}
                        sx={{
                          bgcolor: "white",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "primary.light" },
                        }}
                      >
                        <EditIcon fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDelete(post)}
                        sx={{
                          bgcolor: "white",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "error.light" },
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Stack>
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h6" fontWeight="700" sx={{ mb: 1.2 }}>
                    {post.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      lineHeight: 1.6,
                      WebkitLineClamp: 3,
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {post.excerpt}
                  </Typography>

                  {(post.isFeatured || post.isTrending || post.isLatest) && (
                    <Stack direction="row" spacing={1} mb={1}>
                      {post.isFeatured && (
                        <Chip
                          label="Featured"
                          icon={<BookmarkIcon />}
                          color="primary"
                          size="small"
                        />
                      )}
                      {post.isTrending && (
                        <Chip
                          label="Trending"
                          icon={<TrendingIcon />}
                          color="secondary"
                          size="small"
                        />
                      )}
                      {post.isLatest && (
                        <Chip
                          label="New"
                          icon={<NewIcon />}
                          color="success"
                          size="small"
                        />
                      )}
                    </Stack>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {post.tags.slice(0, 3).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: "6px", fontWeight: 500 }}
                      />
                    ))}
                    {post.tags.length > 3 && (
                      <Chip
                        label={`+${post.tags.length - 3}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      <BlogForm open={open} onClose={handleClose} selected={selected} />
      <ConfirmDelete
        open={deleteOpen}
        onClose={closeDelete}
        deleteFunc={() => dispatch(deleteBlog(selected._id))}
      />
    </Box>
  );
}
